import { type EventState, type EventStatePayload } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { toast, useLatest } from '@solinkcorp/solink-components';
import { AxiosError } from 'axios';
import EventsApi from 'clients/EventsApi';
import { type IEvent } from 'clients/PlatformApi';
import AnalyticsLogger from 'models/AnalyticsLogger';
import React from 'react';
import useCallhomeClient from 'src/hooks/useCallhomeClient';
import useLocale from 'src/hooks/useLocale';
import usePlatformClient from 'src/hooks/usePlatformClient';
import { type EventController } from 'src/hooks/useSolinkEvents';
import useSWR from 'swr';
import { GENERAL_SCHEMA_ID } from './schema';
const SCHEMA_ID = GENERAL_SCHEMA_ID;

export type TransitionEventFlowFn = (params: {
  customerId: string;
  eventId: string;
  workflowId: string;
  payload: EventStatePayload;
}) => Promise<EventState | undefined>;

export default function useEventState({
  eventId,
  workflowId,
  workflowName,
  updateEvent,
  isEventVerified,
  conclusionReason,
  onEventStateUpdated,
  fetchLatestEvent,
  onError,
  transitionEventFlow,
}: {
  eventId: string;
  workflowId?: string;
  workflowName?: string;
  updateEvent: EventController['updateEvent'];
  isEventVerified: boolean;
  conclusionReason?: string;
  onEventStateUpdated?: (
    request: Record<string, unknown>,
    response: Record<string, unknown>,
    parentState: Record<string, unknown>,
  ) => void;
  // Override how the latest event is fetched after state transitions. Defaults
  // to platformClient.getEvent. VerifEye4 passes a Monitor Centre-backed
  // fetcher so it hits GET /verifications/{eventId} instead.
  fetchLatestEvent?: (eventId: string) => Promise<IEvent>;
  // When provided, the hook skips the toast on backend transition failures
  // (initial verification claim + subsequent state updates) and invokes the
  // callback so the caller can surface its own UI (e.g. the V4 escape modal).
  // Other guard toasts (missing workflow/customer id, etc.) still fire.
  onError?: (error: unknown, context: 'startVerification' | 'updateEventState') => void;
  // Override how event-flow state transitions are performed. When provided,
  // the hook routes BOTH the initial verification-claim and subsequent
  // verification-prompt transitions through this function instead of POSTing
  // directly to Callhome. VerifEye4 wires this up to Monitor Centre so MC
  // owns the transition end-to-end and the Callhome/MC double-write is
  // avoided.
  transitionEventFlow?: TransitionEventFlowFn;
}) {
  const user = useUserSession();
  const customerId = user?.profile?.customerId;
  const email = user?.profile?.email;
  const callhomeClient = useCallhomeClient();
  const platformClient = usePlatformClient();
  const locale = useLocale();

  const getLatestEvent = React.useCallback(
    (id: string): Promise<IEvent> =>
      fetchLatestEvent ? fetchLatestEvent(id) : platformClient.getEvent(id),
    [fetchLatestEvent, platformClient],
  );

  const [isResetting, setIsResetting] = React.useState(false);

  const currentIsEventVerified = useLatest(isEventVerified);
  const currentOnError = useLatest(onError);
  const currentTransitionEventFlow = useLatest(transitionEventFlow);

  const {
    data: currentState,
    mutate,
    isLoading: isEventStateLoading,
  } = useSWR(
    ['eventState', eventId, workflowId],
    async () => {
      try {
        if (!workflowId) {
          return;
        }
        if (!customerId) {
          toast.error(locale.eventState.customerIdError);
          return;
        }

        const claimPayload = {
          type: 'verification-claim' as const,
          claimedBy: email ?? 'unknown',
          updatedTime: Date.now(),
        };

        const transition = currentTransitionEventFlow.current;
        if (transition) {
          try {
            return await transition({
              customerId,
              eventId,
              workflowId,
              payload: claimPayload,
            });
          } catch (error) {
            console.error(error);
            if (currentOnError.current) {
              currentOnError.current(error, 'startVerification');
            } else {
              toast.error(locale.eventState.startVerificationError);
            }
            return;
          }
        }

        const state = await callhomeClient.getEventState(eventId, workflowId, SCHEMA_ID);

        // If the workflow isn't started, start it.
        if (!state && currentState?.state !== 'CLOSED' && !currentIsEventVerified.current) {
          try {
            const newState = await callhomeClient.goToNextEventState(
              customerId,
              eventId,
              workflowId,
              SCHEMA_ID,
              claimPayload,
            );

            return newState;
          } catch (error) {
            console.error(error);
            if (currentOnError.current) {
              currentOnError.current(error, 'startVerification');
            } else {
              toast.error(locale.eventState.startVerificationError);
            }
          }
        }

        return state;
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          // if the workflow doesn't exist anymore, then we don't need to show the error
          return;
        }
        toast.error(locale.eventState.fetchEventStateError);

        console.error(error);
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const updateEventState = React.useCallback(
    async (value: string) => {
      if (!workflowId) {
        toast.error(locale.eventState.workflowIdError);
        return;
      }

      if (!currentState) {
        toast.error(locale.eventState.updateEventStateBeforeInitError);
        return;
      }

      if (!customerId) {
        toast.error(locale.eventState.customerIdError);
        return;
      }

      try {
        const requestPayload = {
          type: 'verification-prompt' as const,
          updatedBy: email ?? 'unknown',
          updatedTime: Date.now(),
          transition: value,
        };

        const newState = transitionEventFlow
          ? await transitionEventFlow({
              customerId,
              eventId,
              workflowId,
              payload: requestPayload,
            })
          : await callhomeClient.goToNextEventState(
              customerId,
              eventId,
              workflowId,
              SCHEMA_ID,
              requestPayload,
            );

        if (!newState) {
          toast.error(locale.eventState.updateEventStateError);
          return;
        }

        // Invoke callback if provided (legacy callers use this for side
        // effects like dataset items; V4 no longer needs an MC bridge here).
        if (onEventStateUpdated) {
          onEventStateUpdated(
            { customerId, ...requestPayload },
            newState as unknown as Record<string, unknown>,
            currentState as unknown as Record<string, unknown>,
          );
        }

        if (newState.state === 'CLOSED') {
          try {
            const newEvent = await getLatestEvent(eventId);
            updateEvent(newEvent);
            AnalyticsLogger.eventVerify({
              id: newEvent.id,
              isSuccess: true,
              conclusion: newEvent.details?.verificationConclusion,
              conclusionReason: newEvent.details?.verificationConclusionReason,
              verifiedBy: email,
              schemaId: SCHEMA_ID,
              workflowId,
              workflowName,
            });
          } catch (error) {
            toast.error(locale.eventState.latestEventError);
            console.error(error);
            AnalyticsLogger.eventVerify({
              isSuccess: false,
              verifiedBy: email,
              schemaId: SCHEMA_ID,
              workflowId,
              workflowName,
            });
          }
        }

        mutate(newState, { revalidate: false });
      } catch (error) {
        console.error(error);
        if (currentOnError.current) {
          currentOnError.current(error, 'updateEventState');
        } else {
          toast.error(locale.eventState.updateEventStateError);
        }
      }
    },
    [
      workflowId,
      currentState,
      customerId,
      locale.eventState.workflowIdError,
      locale.eventState.updateEventStateBeforeInitError,
      locale.eventState.customerIdError,
      locale.eventState.latestEventError,
      locale.eventState.updateEventStateError,
      email,
      callhomeClient,
      eventId,
      onEventStateUpdated,
      mutate,
      getLatestEvent,
      updateEvent,
      workflowName,
      currentOnError,
      transitionEventFlow,
    ],
  );

  const resetEventFlowState = React.useCallback(async () => {
    if (isResetting) {
      return;
    }

    setIsResetting(true);

    if (!workflowId) {
      toast.error(locale.eventState.workflowIdError);
      return;
    }

    if (!customerId) {
      toast.error(locale.eventState.customerIdError);
      return;
    }

    try {
      await callhomeClient.resetEventFlowState(eventId, workflowId, SCHEMA_ID, {
        customerId,
      });

      // get the latest event with event flow fields cleared
      const newEvent = await getLatestEvent(eventId);
      updateEvent(newEvent);

      mutate();
      setIsResetting(false);

      AnalyticsLogger.eventFlowTagRemoved({
        workflowId,
        workflowName,
        tag: conclusionReason,
        isSuccess: true,
      });
    } catch (error) {
      toast.error(locale.eventFlow.resetEventFlowStateError);
      console.error(error);
      setIsResetting(false);

      AnalyticsLogger.eventFlowTagRemoved({
        workflowId,
        workflowName,
        tag: conclusionReason,
        isSuccess: false,
      });
    }
  }, [
    callhomeClient,
    eventId,
    workflowId,
    locale.eventFlow.resetEventFlowStateError,
    locale.eventState.workflowIdError,
    locale.eventState.customerIdError,
    mutate,
    customerId,
    updateEvent,
    getLatestEvent,
    isResetting,
    workflowName,
    conclusionReason,
  ]);

  const removeConclusions = React.useCallback(async () => {
    if (!customerId) {
      toast.error(locale.eventState.customerIdError);
      return;
    }

    try {
      await EventsApi.editEvent(
        user.accessToken,
        customerId,
        eventId,
        {
          details: {
            verificationConclusion: null as unknown as undefined,
            verificationConclusionReason: null as unknown as undefined,
            eventFlowId: null as unknown as undefined,
          },
        },
        false,
      );
      const newEvent = await getLatestEvent(eventId);
      updateEvent(newEvent);

      AnalyticsLogger.eventFlowTagRemoved({
        workflowId,
        workflowName,
        isSuccess: true,
        tag: conclusionReason,
      });
    } catch (error) {
      toast.error(locale.eventFlow.resetEventFlowStateError);
      console.error(error);
      AnalyticsLogger.eventFlowTagRemoved({
        workflowId,
        workflowName,
        isSuccess: false,
        tag: conclusionReason,
      });
    }
  }, [
    customerId,
    locale.eventState.customerIdError,
    locale.eventFlow.resetEventFlowStateError,
    eventId,
    updateEvent,
    user.accessToken,
    getLatestEvent,
    workflowId,
    workflowName,
    conclusionReason,
  ]);

  const getEventStateHistory = React.useCallback(async () => {
    if (!workflowId) {
      toast.error(locale.eventState.workflowIdError);
      return;
    }

    const history = await callhomeClient.getEventStateHistory(eventId, workflowId, SCHEMA_ID);
    return history;
  }, [callhomeClient, eventId, workflowId, locale.eventState.workflowIdError]);

  // If the event is verified, but the pulled state is not closed,
  // the workflow is not the original one, so ignore the state.
  // This is to handle the case where the workflow is updated, but the
  // event is not restarted. We want to show the original results
  // until the event is restarted.
  const shouldStateBeIgnored = isEventVerified && currentState?.state !== 'CLOSED';

  return {
    currentState: shouldStateBeIgnored ? undefined : currentState,
    mutate,
    updateEventState,
    resetEventFlowState,
    removeConclusions,
    isEventStateLoading,
    getEventStateHistory,
  };
}
